import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Inbox, RefreshCw, Search, Filter, MapPin, Beaker, Mail, Calendar, Download, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: 7, label: "7 Tage" },
  { value: 30, label: "30 Tage" },
  { value: 90, label: "90 Tage" },
  { value: 0, label: "Alle" },
];

const BUNDESLAENDER = [
  ["", "Alle Bundesländer"],
  ["BW", "Baden-Württemberg"], ["BY", "Bayern"], ["BE", "Berlin"],
  ["BB", "Brandenburg"], ["HB", "Bremen"], ["HH", "Hamburg"],
  ["HE", "Hessen"], ["MV", "Mecklenburg-Vorpommern"], ["NI", "Niedersachsen"],
  ["NW", "Nordrhein-Westfalen"], ["RP", "Rheinland-Pfalz"], ["SL", "Saarland"],
  ["SN", "Sachsen"], ["ST", "Sachsen-Anhalt"], ["SH", "Schleswig-Holstein"],
  ["TH", "Thüringen"],
];

const VARIANTS = [
  ["", "Alle Varianten"],
  ["A", "Editorial (A)"],
  ["B", "Direct-Response (B)"],
];

function fmtDate(iso) {
  if (!iso) return "–";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function toCsv(rows) {
  const cols = ["created_at", "email", "name", "role", "magnet_id", "magnet_title", "bundesland", "variant", "newsletter_opt_in", "source"];
  const header = cols.join(";");
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[;"\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = rows.map((r) => cols.map((c) => esc(r[c])).join(";"));
  return [header, ...lines].join("\n");
}

function toKontaktCsv(rows) {
  const cols = ["created_at", "name", "email", "phone", "audience", "course_interest", "message", "source"];
  const header = cols.join(";");
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[;"\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = rows.map((r) => cols.map((c) => esc(r[c])).join(";"));
  return [header, ...lines].join("\n");
}

/**
 * Admin-Inbox aller Lead-Magnet-Eintragungen.
 * Datenquelle: GET /api/admin/leadmagnet/leads?days=&bundesland=&variant=&q=
 */
export default function CampusLeadsInbox() {
  const { user } = useCampusAuth();
  const [tab, setTab] = useState("kontakt");
  const [days, setDays] = useState(30);
  const [bundesland, setBundesland] = useState("");
  const [variant, setVariant] = useState("");
  const [q, setQ] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 500 };
      if (days > 0) params.days = days;
      if (bundesland) params.bundesland = bundesland;
      if (variant) params.variant = variant;
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${API}/admin/leadmagnet/leads`, {
        params, withCredentials: true,
      });
      setLeads(res.data?.leads || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Konnte Leads nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [days, bundesland, variant]);

  const stats = useMemo(() => {
    const newsletter = leads.filter((l) => l.newsletter_opt_in).length;
    const uniqEmails = new Set(leads.map((l) => (l.email || "").toLowerCase())).size;
    return { total: leads.length, newsletter, uniqEmails };
  }, [leads]);

  const downloadCsv = () => {
    const csv = toCsv(leads);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `dh-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!user?.is_admin) {
    return (
      <div data-testid="leads-inbox-locked" className="rounded-3xl border border-cream-300 bg-white p-8 text-sand-700">
        Diese Seite ist nur für Admins sichtbar.
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="leads-inbox-page">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Inbox className="h-5 w-5 text-terra" strokeWidth={1.8} />
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay-dark">
              Admin · Leads-Inbox
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-sand-800 leading-tight">
            Wer hat sich gerade eingetragen?
          </h1>
          <p className="mt-2 text-sand-600 text-[14.5px] max-w-2xl">
            Alle Lead-Magnet-Eintragungen aus den Funnels und Ads-Landingpages – mit Filter
            nach Zeitraum, Bundesland und A/B-Variante. CSV-Export für Stripe/CRM.
          </p>
        </div>
        {tab === "magnet" && (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={load} disabled={loading} variant="outline"
              data-testid="leads-inbox-reload"
              className="rounded-full h-11 px-5 border-clay text-sand-800 hover:bg-cream-100">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Neu laden
            </Button>
            <Button onClick={downloadCsv} disabled={!leads.length}
              data-testid="leads-inbox-csv"
              className="rounded-full h-11 px-5 bg-sand-800 hover:bg-sand-700 text-cream-50">
              <Download className="h-4 w-4 mr-2" /> CSV
            </Button>
          </div>
        )}
      </div>

      {/* TABS */}
      <div className="flex gap-2" data-testid="leads-inbox-tabs">
        <TabBtn active={tab === "kontakt"} onClick={() => setTab("kontakt")} testid="leads-tab-kontakt">
          Kontaktanfragen
        </TabBtn>
        <TabBtn active={tab === "magnet"} onClick={() => setTab("magnet")} testid="leads-tab-magnet">
          Leadmagnet-Leads
        </TabBtn>
      </div>

      {tab === "kontakt" ? (
        <KontaktAnfragen />
      ) : (
        <>
      {/* KPI */}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="leads-inbox-kpi">
        <KpiCard label="Leads im Filter" value={stats.total} accent="text-sand-800" />
        <KpiCard label="Unique E-Mails" value={stats.uniqEmails} accent="text-terra" />
        <KpiCard label="Newsletter-Opt-In" value={stats.newsletter} accent="text-brand-darkred" />
      </div>

      {/* FILTER */}
      <div data-testid="leads-inbox-filter"
        className="rounded-3xl border border-cream-300 bg-white p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-terra" strokeWidth={1.8} />
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark">
            Filter
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
              <Calendar className="inline h-3.5 w-3.5 mr-1" /> Zeitraum
            </label>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              data-testid="leads-inbox-days"
              className="w-full h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra">
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
              <MapPin className="inline h-3.5 w-3.5 mr-1" /> Bundesland
            </label>
            <select value={bundesland} onChange={(e) => setBundesland(e.target.value)}
              data-testid="leads-inbox-bundesland"
              className="w-full h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra">
              {BUNDESLAENDER.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
              <Beaker className="inline h-3.5 w-3.5 mr-1" /> A/B-Variante
            </label>
            <select value={variant} onChange={(e) => setVariant(e.target.value)}
              data-testid="leads-inbox-variant"
              className="w-full h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra">
              {VARIANTS.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
              <Search className="inline h-3.5 w-3.5 mr-1" /> Suche (Name/E-Mail)
            </label>
            <div className="flex gap-1.5">
              <input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                data-testid="leads-inbox-q"
                placeholder="z. B. mueller oder @gmail"
                className="flex-1 h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra" />
              <Button onClick={load} disabled={loading}
                data-testid="leads-inbox-search"
                className="rounded-xl h-10 px-3 bg-terra hover:bg-terra-dark text-white">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div data-testid="leads-inbox-error"
          className="rounded-2xl border border-terra/40 bg-terra/10 text-terra-dark p-4">
          {error}
        </div>
      )}

      {/* TABLE */}
      <div data-testid="leads-inbox-table"
        className="rounded-3xl border border-cream-300 bg-white overflow-hidden">
        {leads.length === 0 && !loading ? (
          <div className="p-10 text-center text-sand-500">
            Keine Leads im aktuellen Filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-cream-50 border-b border-cream-300">
                <tr className="text-left text-[10.5px] uppercase tracking-[0.18em] text-sand-500">
                  <th className="px-4 py-3 font-semibold">Zeit</th>
                  <th className="px-4 py-3 font-semibold">E-Mail</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Leitfaden</th>
                  <th className="px-4 py-3 font-semibold">BL</th>
                  <th className="px-4 py-3 font-semibold">A/B</th>
                  <th className="px-4 py-3 font-semibold">NL</th>
                  <th className="px-4 py-3 font-semibold text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id || `${l.email}-${l.created_at}`}
                    data-testid={`leads-inbox-row-${l.email}`}
                    className="border-b border-cream-200 last:border-0 hover:bg-cream-50">
                    <td className="px-4 py-3 text-sand-600 whitespace-nowrap tabular-nums">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-sand-800 font-medium">
                      <a href={`mailto:${l.email}`} className="hover:text-terra inline-flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 opacity-60" />{l.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sand-600">{l.name || "–"}</td>
                    <td className="px-4 py-3 text-sand-600 max-w-[280px] truncate" title={l.magnet_title}>
                      {l.magnet_title || l.magnet_id || "–"}
                    </td>
                    <td className="px-4 py-3">
                      {l.bundesland ? (
                        <span className="inline-flex items-center justify-center rounded-md bg-sand-800 text-cream-50 px-2 h-6 text-[10px] font-bold">
                          {l.bundesland}
                        </span>
                      ) : <span className="text-sand-400">–</span>}
                    </td>
                    <td className="px-4 py-3">
                      {l.variant === "A" || l.variant === "B" ? (
                        <span className={`inline-flex items-center justify-center rounded-full px-2 h-6 text-[10px] font-bold ${l.variant === "A" ? "bg-terra/15 text-terra" : "bg-sage-dark/15 text-sage-dark"}`}>
                          {l.variant}
                        </span>
                      ) : <span className="text-sand-400">–</span>}
                    </td>
                    <td className="px-4 py-3">
                      {l.newsletter_opt_in ? (
                        <span className="inline-flex items-center rounded-full bg-sage-light text-sage-dark px-2 h-5 text-[10px] font-bold">JA</span>
                      ) : (
                        <span className="text-sand-400">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ResendButton lead={l} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[12px] text-sand-500">
        Maximal 500 Leads pro Abfrage · sortiert nach Eintragungs-Zeit (neueste zuerst).
        Felder <em>BL</em> & <em>A/B</em> werden aus dem <code>source</code>-Parameter der Ads-LPs gelesen.
      </p>
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-full h-10 px-5 text-[12px] font-bold uppercase tracking-[0.12em] transition-colors ${
        active ? "bg-sand-800 text-cream-50" : "bg-white border border-cream-300 text-sand-600 hover:border-terra/50 hover:text-terra"
      }`}>
      {children}
    </button>
  );
}

function KontaktAnfragen() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/leads`, { params: { limit: 500 }, withCredentials: true });
      setRows(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Konnte Kontaktanfragen nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.name, r.email, r.phone, r.course_interest, r.message, r.source]
        .some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const downloadCsv = () => {
    const blob = new Blob(["\uFEFF" + toKontaktCsv(filtered)], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `dh-kontaktanfragen-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const AUDIENCE_LABELS = { pflegekraft: "Pflegekraft", pflegedienst: "Pflegedienst", agentur: "Agentur" };

  return (
    <div className="space-y-6" data-testid="kontakt-anfragen-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1 min-w-[220px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-sand-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="kontakt-anfragen-q"
              placeholder="Suche: Name, E-Mail, Kurs, Nachricht …"
              className="w-full h-11 rounded-full border border-cream-300 bg-white pl-10 pr-4 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} disabled={loading} variant="outline"
            data-testid="kontakt-anfragen-reload"
            className="rounded-full h-11 px-5 border-clay text-sand-800 hover:bg-cream-100">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Neu laden
          </Button>
          <Button onClick={downloadCsv} disabled={!filtered.length}
            data-testid="kontakt-anfragen-csv"
            className="rounded-full h-11 px-5 bg-sand-800 hover:bg-sand-700 text-cream-50">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
        </div>
      </div>

      {error && (
        <div data-testid="kontakt-anfragen-error"
          className="rounded-2xl border border-terra/40 bg-terra/10 text-terra-dark p-4">
          {error}
        </div>
      )}

      <div data-testid="kontakt-anfragen-table" className="rounded-3xl border border-cream-300 bg-white overflow-hidden">
        {filtered.length === 0 && !loading ? (
          <div className="p-10 text-center text-sand-500">Keine Kontaktanfragen gefunden.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-cream-50 border-b border-cream-300">
                <tr className="text-left text-[10.5px] uppercase tracking-[0.18em] text-sand-500">
                  <th className="px-4 py-3 font-semibold">Zeit</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Kontakt</th>
                  <th className="px-4 py-3 font-semibold">Ich bin</th>
                  <th className="px-4 py-3 font-semibold">Kurs-Interesse</th>
                  <th className="px-4 py-3 font-semibold">Nachricht</th>
                  <th className="px-4 py-3 font-semibold">Quelle</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} data-testid={`kontakt-anfrage-row-${l.email}`}
                    onClick={() => setSelected(l)}
                    className="border-b border-cream-200 last:border-0 hover:bg-cream-50 align-top cursor-pointer">
                    <td className="px-4 py-3 text-sand-600 whitespace-nowrap tabular-nums">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-sand-800 font-medium whitespace-nowrap">{l.name || "–"}</td>
                    <td className="px-4 py-3 text-sand-600">
                      <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-terra inline-flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 opacity-60" />{l.email}
                      </a>
                      {l.phone && <div className="text-[12px] text-sand-500 mt-0.5">{l.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-sand-600 whitespace-nowrap">{AUDIENCE_LABELS[l.audience] || l.audience || "–"}</td>
                    <td className="px-4 py-3 text-sand-600 max-w-[200px]">{l.course_interest || "–"}</td>
                    <td className="px-4 py-3 text-sand-600 max-w-[320px]">
                      <div className="line-clamp-3 whitespace-pre-wrap" title={l.message}>{l.message || "–"}</div>
                    </td>
                    <td className="px-4 py-3 text-sand-500 max-w-[140px] truncate" title={l.source}>{l.source || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[12px] text-sand-500">
        Zeile anklicken, um die vollständige Anfrage zu lesen · neueste zuerst.
        Neue Anfragen lösen zusätzlich eine E-Mail-Benachrichtigung aus.
      </p>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="kontakt-anfrage-detail" className="max-w-xl bg-cream-50 border-cream-300 rounded-3xl p-0 overflow-hidden">
          {selected && (
            <>
              <DialogHeader className="px-7 pt-7 pb-0">
                <div className="text-[10px] uppercase tracking-[0.26em] text-terra font-bold mb-1.5">
                  Kontaktanfrage · {fmtDate(selected.created_at)}
                </div>
                <DialogTitle className="font-magazine text-[1.6rem] font-medium text-sand-800 text-left">
                  {selected.name || "Ohne Name"}
                </DialogTitle>
              </DialogHeader>
              <div className="px-7 pb-7 pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <DetailField label="E-Mail" value={selected.email} href={`mailto:${selected.email}`} />
                  <DetailField label="Telefon" value={selected.phone} href={selected.phone ? `tel:${selected.phone}` : null} />
                  <DetailField label="Ich bin" value={{ pflegekraft: "Pflegekraft", pflegedienst: "Pflegedienst", agentur: "Agentur" }[selected.audience] || selected.audience} />
                  <DetailField label="Kurs-Interesse" value={selected.course_interest} />
                  <DetailField label="Quelle" value={selected.source} />
                </div>
                <div className="rounded-2xl bg-white border border-cream-300 p-5">
                  <div className="text-[9.5px] uppercase tracking-[0.26em] text-sand-500 font-bold mb-2">Nachricht</div>
                  <p data-testid="kontakt-anfrage-detail-message" className="text-[14px] text-sand-800 leading-[1.75] whitespace-pre-wrap">
                    {selected.message || "– keine Nachricht hinterlassen –"}
                  </p>
                </div>
                <Button asChild className="w-full rounded-full bg-terra hover:bg-terra-dark text-cream-50 h-12 text-[12.5px] font-bold uppercase tracking-[0.12em]">
                  <a href={`mailto:${selected.email}?subject=${encodeURIComponent("Ihre Anfrage bei Dickies-Campus")}`} data-testid="kontakt-anfrage-reply">
                    <Send className="h-4 w-4 mr-2" /> Antworten
                  </a>
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailField({ label, value, href }) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-[0.26em] text-sand-500 font-bold">{label}</div>
      {href && value ? (
        <a href={href} className="text-[13.5px] text-terra hover:underline break-words">{value}</a>
      ) : (
        <div className="text-[13.5px] text-sand-800 break-words">{value || "–"}</div>
      )}
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div className="rounded-3xl border border-cream-300 bg-white p-6">
      <div className={`font-display text-4xl ${accent}`}>{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-sand-500">{label}</div>
    </div>
  );
}

/**
 * ResendButton · admin-only re-send der Welcome-Mail (Atlas-Vorschau etc.)
 * Nutzt POST /api/admin/leadmagnet/resend.
 */
function ResendButton({ lead }) {
  const [busy, setBusy] = useState(false);
  const magnetId = lead?.magnet_id;
  const disabled = !magnetId || magnetId === "kurskatalog-pdf";
  const handle = async () => {
    if (!magnetId) return;
    const ok = window.confirm(
      `Welcome-Mail für "${lead.magnet_title || magnetId}" erneut an ${lead.email} senden?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/leadmagnet/resend`,
        { email: lead.email, magnet_id: magnetId, name: lead.name || null },
        { withCredentials: true },
      );
      toast.success(`Mail erneut versendet an ${lead.email}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Re-Send fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handle}
      disabled={busy || disabled}
      data-testid={`leads-inbox-resend-${lead.email}`}
      title={disabled ? "Re-Send für Kurskatalog nicht verfügbar" : "Vorschau-Mail erneut senden"}
      className="text-clay-dark hover:text-terra"
    >
      <Send className="h-3.5 w-3.5 mr-1.5" /> Re-Send
    </Button>
  );
}

